import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Calendar, Package as PackageIcon, Clock, AlertTriangle } from 'lucide-react'
import { localizeError } from '@/lib/api'
import i18n from '@/i18n'

const H5_BASE = '/api/h5'

async function h5Fetch<T>(token: string, path: string): Promise<T> {
  const res = await fetch(`${H5_BASE}/${token}${path}`)
  if (!res.ok) throw new Error(i18n.t('errors.http', { status: res.status }))
  return res.json()
}

function parseTime(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

function formatTime(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function toISODate(date: Date) {
  return date.toISOString().split('T')[0]
}

type Tab = 'appts' | 'pkgs' | 'avail'

interface AppointmentItem {
  id: number
  scheduledDate: number
  status: string
  startTime: string
  endTime: string
  title?: string
  location?: string
}

interface PackageItem {
  id: number
  status: string
  remainingSessions: number
  totalSessions: number
}

export function H5Page() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const [tab, setTab] = useState<Tab>('appts')

  if (!token) return <div className="p-4">{t('h5.invalidLink')}</div>

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
      <div className="max-w-2xl mx-auto p-4">
        <header className="text-center py-4 mb-4">
          <h1 className="text-2xl font-bold text-slate-900">{t('app.name')}</h1>
          <p className="text-sm text-slate-500">{t('h5.portalTitle')}</p>
          <IdentityLine token={token} />
        </header>

        <div className="flex gap-1 mb-4 bg-white p-1 rounded-lg border">
          {[
            { id: 'appts', label: t('h5.tabs.sessions'), icon: Calendar },
            { id: 'pkgs', label: t('h5.tabs.packages'), icon: PackageIcon },
            { id: 'avail', label: t('h5.tabs.availability'), icon: Clock },
          ].map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id as Tab)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium ${
                  tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            )
          })}
        </div>

        {tab === 'appts' && <ApptsTab token={token} />}
        {tab === 'pkgs' && <PackagesTab token={token} />}
        {tab === 'avail' && <AvailabilityTab token={token} />}
      </div>
    </div>
  )
}

interface CoachInfo {
  coach: { id: string; displayName: string | null }
  client: { id: string; name: string }
}

/** OPT-049：页头身份行「学员：X ｜ 教练：Y」；教练名为空仅显示学员部分；token 无效显示 invalidLink */
function IdentityLine({ token }: { token: string }) {
  const { t } = useTranslation()
  const { data, error, isLoading } = useQuery({
    queryKey: ['h5-coach', token],
    queryFn: () => h5Fetch<CoachInfo>(token, '/coach'),
  })

  if (isLoading) return null
  if (error) {
    return <p className="text-sm text-red-500 mt-1">{t('h5.invalidLink')}</p>
  }
  if (!data) return null

  const coachName = data.coach.displayName?.trim()
  return (
    <p className="text-sm text-slate-600 mt-1" data-testid="h5-identity-line">
      {coachName
        ? t('h5.identityLine', { client: data.client.name, coach: coachName })
        : t('h5.identityLineClientOnly', { client: data.client.name })}
    </p>
  )
}

function ApptsTab({ token }: { token: string }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useQuery({
    queryKey: ['h5-appts', token],
    queryFn: () => h5Fetch<{ items: AppointmentItem[] }>(token, '/appointments'),
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox message={(error as Error).message} />

  const items = data?.items || []
  const upcoming = items.filter((a) => a.status === 'scheduled' && a.scheduledDate >= Math.floor(Date.now() / 1000))
  const past = items.filter((a) => a.status !== 'scheduled' || a.scheduledDate < Math.floor(Date.now() / 1000))

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-slate-700">{t('h5.upcoming')}</h2>
      {upcoming.length === 0 ? <Empty msg={t('h5.noUpcoming')} /> : upcoming.map((a) => <ApptCard key={a.id} appt={a} />)}
      {past.length > 0 && <>
        <h2 className="text-sm font-semibold text-slate-700 mt-4">{t('h5.past')}</h2>
        {past.map((a) => <ApptCard key={a.id} appt={a} />)}
      </>}
    </div>
  )
}

function ApptCard({ appt }: { appt: AppointmentItem }) {
  const { t } = useTranslation()
  // OPT-012：学员请假/取消入口已移除；历史 cancelled/late_cancelled 仅以徽章只读展示

  const date = new Date(appt.scheduledDate * 1000)
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="text-center min-w-14">
          <div className="text-xs text-slate-500">{date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
          <div className="text-lg font-bold">{appt.startTime}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium">{appt.title || t('h5.session')}</div>
          <div className="text-sm text-slate-500">
            {appt.startTime} – {appt.endTime}
            {appt.location && <> · {appt.location}</>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant={appt.status === 'completed' ? 'success' : appt.status === 'cancelled' || appt.status === 'late_cancelled' ? 'secondary' : appt.status === 'no_show' ? 'danger' : 'default'}>
            {t(`h5.status.${appt.status}`)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}

function PackagesTab({ token }: { token: string }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useQuery({
    queryKey: ['h5-pkgs', token],
    queryFn: () => h5Fetch<{ items: PackageItem[] }>(token, '/packages'),
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox message={(error as Error).message} />

  const items = data?.items || []
  if (items.length === 0) return <Empty msg={t('h5.noPackages')} />

  return (
    <div className="space-y-2">
      {items.map((p) => {
        const pct = p.totalSessions > 0 ? (p.remainingSessions / p.totalSessions) * 100 : 0
        const color = pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500'
        return (
          <Card key={p.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium">{t('h5.package')}</div>
                <Badge variant={p.status === 'active' ? 'success' : 'secondary'}>{t(`packages.status.${p.status}`)}</Badge>
              </div>
              <div className="text-sm text-slate-600 mb-2">
                {t('h5.sessionsLeft', { remaining: p.remainingSessions, total: p.totalSessions })}
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

function AvailabilityTab({ token }: { token: string }) {
  const { t } = useTranslation()
  const [date, setDate] = useState<string>(toISODate(new Date()))

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['h5-avail', token, date],
    queryFn: () =>
      h5Fetch<{ slots: string[] }>(
        token,
        `/availability?date=${date}`,
      ),
    enabled: !!date,
  })

  // 服务类型移除后，约课时长固定 60 分钟（与服务端 slot 粒度一致）
  const SLOT_MINUTES = 60

  const handleBook = async (startTime: string) => {
    if (!confirm(`${t('h5.book')} ${startTime}?`)) return
    const endTime = formatTime(parseTime(startTime) + SLOT_MINUTES)
    try {
      const res = await fetch(`${H5_BASE}/${token}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduledDate: date,
          startTime,
          endTime,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        alert(localizeError(err.error))
      } else {
        alert(t('h5.bookSuccess'))
        refetch()
      }
    } catch {
      alert(t('common.networkError'))
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">{t('h5.selectDate')}</label>
        <input
          type="date"
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={date}
          min={toISODate(new Date())}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <p className="text-sm text-slate-500">{t('h5.slotsHint')}</p>

      {isLoading && <Loading />}
      {error && <ErrorBox message={(error as Error).message} />}
      {data && data.slots.length === 0 && <Empty msg={t('h5.noSlots')} />}
      {data && data.slots.map((s) => (
        <Card key={s}>
          <CardContent className="p-3 flex items-center justify-between text-sm">
            <div>
              <Clock className="h-4 w-4 inline mr-2 text-slate-400" />
              {s}
              <span className="text-slate-400 ml-2">
                – {formatTime(parseTime(s) + SLOT_MINUTES)}
              </span>
            </div>
            <Button size="sm" onClick={() => handleBook(s)}>{t('h5.book')}</Button>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function Loading() {
  const { t } = useTranslation()
  return <Card><CardContent className="p-8 text-center text-slate-500">{t('common.loading')}</CardContent></Card>
}

function Empty({ msg }: { msg: string }) {
  return <Card><CardContent className="p-8 text-center text-slate-500">{msg}</CardContent></Card>
}

function ErrorBox({ message }: { message: string }) {
  const { t } = useTranslation()
  return <Card><CardContent className="p-4 text-red-600">
    <AlertTriangle className="h-4 w-4 inline mr-2" /> {message || t('errors.default')}
  </CardContent></Card>
}
